import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { 
	ShoppingCart, 
	ArrowLeft, 
	Eye, 
	Heart, 
	Star, 
	MessageSquare, 
	ChevronLeft, 
	ChevronRight,
	Shield,
	Truck,
	RotateCcw,
	Award,
	Sparkles,
	Zap,
	Info,
	Check,
	Minus,
	Plus,
	Share2,
	Bookmark
} from "lucide-react";
import { useUserStore } from "../stores/useUserStore";
import { useCartStore } from "../stores/useCartStore";
import { useReviewStore } from "../stores/useReviewStore";
import { useRealtimeData } from "../components/ApiManager";
import { getImageUrl, getProductImages } from "../lib/imageUtils";

import toast from "react-hot-toast";
import LoadingSpinner from "../components/LoadingSpinner";
import ReviewCard from "../components/ReviewCard";
import WriteReviewForm from "../components/WriteReviewForm";

const ProductPage = () => {
	const { productId } = useParams();
	const navigate = useNavigate();
	const { user } = useUserStore();
	const { addToCart } = useCartStore();
	const { reviews, productStats, getProductReviews, voteOnReview, deleteReview } = useReviewStore();
	
	// שימוש ב-real-time data למוצר
	const { data: productResponse, loading, error, refetch } = useRealtimeData(`/products/${productId}`, 60000);
	
	// חילוץ המוצר מהתגובה
	const product = productResponse?.success ? productResponse.product : productResponse;
	
	const [selectedImage, setSelectedImage] = useState(0);
	const [showWriteReview, setShowWriteReview] = useState(false);
	const [quantity, setQuantity] = useState(1);
	const [isWishlisted, setIsWishlisted] = useState(false);
	const [activeTab, setActiveTab] = useState('description');
	const [showImageModal, setShowImageModal] = useState(false);

	// קבלת תמונות המוצר
	const images = product ? getProductImages(product) : [];
	
	// Debug תמונות
	useEffect(() => {
		if (product) {
			console.log('🖼️ Product data:', product);
			console.log('🖼️ Product images array:', product.images);
			console.log('🖼️ Product imageUrl:', product.imageUrl);
			console.log('🖼️ Processed images:', images);
		}
	}, [product, images]);

	// טעינת ביקורות המוצר
	useEffect(() => {
		if (productId) {
			getProductReviews(productId);
		}
	}, [productId, getProductReviews]);

	const handleAddToCart = useCallback(() => {
		addToCart(product);
		toast.success(`${quantity} מוצרים נוספו לעגלה!`);
	}, [addToCart, product, quantity]);

	const handleWishlistToggle = useCallback(() => {
		setIsWishlisted(!isWishlisted);
		toast.success(isWishlisted ? 'הוסר מהמועדפים' : 'נוסף למועדפים');
	}, [isWishlisted]);

	const handleWriteReviewSuccess = useCallback(() => {
		setShowWriteReview(false);
		getProductReviews(productId);
	}, [getProductReviews, productId]);

	const handleVoteOnReview = useCallback((reviewId, voteType) => {
		// TODO: implement vote functionality
	}, []);

	const handleDeleteReview = useCallback((reviewId) => {
		deleteReview(reviewId);
	}, [deleteReview]);

	const handleShare = useCallback(() => {
		if (navigator.share) {
			navigator.share({
				title: product.name,
				text: product.description,
				url: window.location.href,
			});
		} else {
			navigator.clipboard.writeText(window.location.href);
			toast.success('קישור הועתק ללוח');
		}
	}, [product]);

	// הצגת error אם יש שגיאה
	if (error) {
		return (
			<div className="min-h-screen flex items-center justify-center bg-gray-50 pt-20">
				<motion.div 
					className="text-center bg-white p-8 rounded-2xl shadow-lg max-w-md"
					initial={{ opacity: 0, scale: 0.9 }}
					animate={{ opacity: 1, scale: 1 }}
				>
					<div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
						<Info className="w-8 h-8 text-red-600" />
					</div>
					<h2 className="text-2xl font-bold text-gray-900 mb-4">שגיאה בטעינת המוצר</h2>
					<p className="text-gray-600 mb-6">אירעה שגיאה בטעינת פרטי המוצר. אנא נסה שוב.</p>
					<div className="flex gap-3 justify-center">
						<button 
							onClick={refetch}
							className="bg-purple-600 text-white px-6 py-3 rounded-xl hover:bg-purple-700 transition-colors font-medium"
						>
							נסה שוב
						</button>
						<button
							onClick={() => navigate("/")}
							className="bg-gray-200 text-gray-700 px-6 py-3 rounded-xl hover:bg-gray-300 transition-colors font-medium"
						>
							חזור לדף הבית
						</button>
					</div>
				</motion.div>
			</div>
		);
	}

	if (loading) {
		return (
			<div className="min-h-screen flex items-center justify-center bg-gray-50 pt-20">
				<LoadingSpinner />
			</div>
		);
	}

	if (!product) {
		return (
			<div className="min-h-screen flex items-center justify-center bg-gray-50 pt-20">
				<motion.div 
					className="text-center bg-white p-8 rounded-2xl shadow-lg max-w-md"
					initial={{ opacity: 0, scale: 0.9 }}
					animate={{ opacity: 1, scale: 1 }}
				>
					<div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
						<Eye className="w-8 h-8 text-gray-400" />
					</div>
					<h2 className="text-2xl font-bold text-gray-900 mb-4">מוצר לא נמצא</h2>
					<p className="text-gray-600 mb-6">המוצר שחיפשת אינו קיים במערכת.</p>
					<button
						onClick={() => navigate("/")}
						className="bg-purple-600 text-white px-6 py-3 rounded-xl hover:bg-purple-700 transition-colors font-medium"
					>
						חזור לדף הבית
					</button>
				</motion.div>
			</div>
		);
	}

	const price = product.price || 0;
	const originalPrice = product.originalPrice || price;
	const hasDiscount = originalPrice > price;
	const discountPercentage = hasDiscount ? Math.round(((originalPrice - price) / originalPrice) * 100) : 0;

	return (
		<div className="min-h-screen bg-gray-50 pt-20">
			{/* Breadcrumb + כפתור חזרה */}
			<div className="bg-white border-b border-gray-200">
				<div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
					<motion.button
						onClick={() => navigate(-1)}
						className="flex items-center gap-2 text-gray-600 hover:text-purple-600 transition-colors font-medium"
						initial={{ opacity: 0, x: -20 }}
						animate={{ opacity: 1, x: 0 }}
						whileHover={{ x: -2 }}
					>
						<ArrowLeft className="w-5 h-5" />
						חזור
					</motion.button>
				</div>
			</div>

			<div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
				<div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
					{/* אזור התמונות */}
					<motion.div
						className="space-y-4"
						initial={{ opacity: 0, y: 30 }}
						animate={{ opacity: 1, y: 0 }}
						transition={{ duration: 0.6 }}
					>
						{/* תמונה ראשית */}
						<div className="relative bg-white rounded-2xl overflow-hidden shadow-lg group">
							<motion.img
								src={images[selectedImage]}
								alt={product.name}
								className="w-full aspect-square object-cover cursor-zoom-in transition-transform duration-700 group-hover:scale-105"
								onClick={() => setShowImageModal(true)}
								whileHover={{ scale: 1.02 }}
								onLoad={() => {
									console.log('✅ Main image loaded successfully:', images[selectedImage]);
								}}
								onError={(e) => {
									console.error('❌ Main image failed to load:', images[selectedImage]);
									console.error('❌ Error details:', e);
									e.target.src = '/images/placeholder.svg';
								}}
							/>
							
							{/* תוויות סטטוס */}
							<div className="absolute top-6 right-6 flex flex-col gap-3">
								{product.isNewProduct && (
									<motion.span 
										className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-emerald-500 to-emerald-600 text-white text-sm font-bold rounded-full shadow-lg"
										initial={{ scale: 0, rotate: -10 }}
										animate={{ scale: 1, rotate: 0 }}
										transition={{ delay: 0.3 }}
									>
										<Sparkles className="w-4 h-4" />
										חדש
									</motion.span>
								)}
								{hasDiscount && (
									<motion.span 
										className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-red-500 to-pink-600 text-white text-sm font-bold rounded-full shadow-lg"
										initial={{ scale: 0, rotate: 10 }}
										animate={{ scale: 1, rotate: 0 }}
										transition={{ delay: 0.4 }}
									>
										<Zap className="w-4 h-4" />
										{discountPercentage}% הנחה
									</motion.span>
								)}
								{product.isBestSeller && (
									<motion.span 
										className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-amber-500 to-orange-600 text-white text-sm font-bold rounded-full shadow-lg"
										initial={{ scale: 0, rotate: -5 }}
										animate={{ scale: 1, rotate: 0 }}
										transition={{ delay: 0.5 }}
									>
										<Award className="w-4 h-4" />
										רב מכר
									</motion.span>
								)}
							</div>

							{/* ניווט תמונות */}
							{images.length > 1 && (
								<>
									<button
										onClick={() => setSelectedImage((prev) => (prev - 1 + images.length) % images.length)}
										className="absolute left-4 top-1/2 -translate-y-1/2 p-3 bg-white/90 backdrop-blur-sm rounded-full shadow-lg hover:bg-white transition-all duration-200 opacity-0 group-hover:opacity-100"
									>
										<ChevronLeft className="w-6 h-6 text-gray-700" />
									</button>
									<button
										onClick={() => setSelectedImage((prev) => (prev + 1) % images.length)}
										className="absolute right-4 top-1/2 -translate-y-1/2 p-3 bg-white/90 backdrop-blur-sm rounded-full shadow-lg hover:bg-white transition-all duration-200 opacity-0 group-hover:opacity-100"
									>
										<ChevronRight className="w-6 h-6 text-gray-700" />
									</button>
								</>
							)}

							{/* נקודות ניווט */}
							{images.length > 1 && (
								<div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex gap-2">
									{images.map((_, index) => (
										<button
											key={index}
											onClick={() => setSelectedImage(index)}
											className={`w-3 h-3 rounded-full transition-all duration-200 ${
												index === selectedImage 
													? 'bg-white shadow-lg scale-125' 
													: 'bg-white/60 hover:bg-white/80'
											}`}
										/>
									))}
								</div>
							)}
						</div>

						{/* גלריה קטנה */}
						{images.length > 1 && (
							<div className="flex gap-3 overflow-x-auto pb-2">
								{images.map((image, index) => (
									<motion.button
										key={index}
										onClick={() => setSelectedImage(index)}
										className={`flex-shrink-0 w-20 h-20 rounded-xl overflow-hidden border-2 transition-all duration-200 ${
											selectedImage === index 
												? 'border-purple-500 shadow-lg scale-105' 
												: 'border-gray-200 hover:border-gray-300'
										}`}
										whileHover={{ scale: selectedImage === index ? 1.05 : 1.02 }}
										whileTap={{ scale: 0.95 }}
									>
										<img
											src={image}
											alt={`${product.name} ${index + 1}`}
											className="w-full h-full object-cover"
											onLoad={() => {
												console.log(`✅ Gallery image ${index + 1} loaded:`, image);
											}}
											onError={(e) => {
												console.error(`❌ Gallery image ${index + 1} failed:`, image);
												e.target.src = '/images/placeholder.svg';
											}}
										/>
									</motion.button>
								))}
							</div>
						)}
					</motion.div>

					{/* פרטי המוצר */}
					<motion.div
						className="space-y-6"
						initial={{ opacity: 0, y: 30 }}
						animate={{ opacity: 1, y: 0 }}
						transition={{ duration: 0.6, delay: 0.2 }}
					>
						{/* כותרת ופעולות */}
						<div className="space-y-4">
							<div className="flex items-start justify-between">
								<div className="flex-1">
									<h1 className="text-3xl lg:text-4xl font-bold text-gray-900 leading-tight">
										{product.name}
									</h1>
									<p className="text-gray-500 mt-2 font-medium">SKU: {product.sku}</p>
								</div>
								
								{/* כפתורי פעולה */}
								<div className="flex gap-3 ml-4">
									<motion.button
										onClick={handleWishlistToggle}
										className={`p-3 rounded-xl transition-all duration-300 ${
											isWishlisted 
												? 'bg-red-50 text-red-600 shadow-lg shadow-red-500/20' 
												: 'bg-gray-100 text-gray-600 hover:bg-red-50 hover:text-red-500'
										}`}
										whileHover={{ scale: 1.05 }}
										whileTap={{ scale: 0.95 }}
									>
										<Heart className={`w-6 h-6 ${isWishlisted ? 'fill-current' : ''}`} />
									</motion.button>
									
									<motion.button
										onClick={handleShare}
										className="p-3 bg-gray-100 text-gray-600 rounded-xl hover:bg-purple-50 hover:text-purple-600 transition-colors"
										whileHover={{ scale: 1.05 }}
										whileTap={{ scale: 0.95 }}
									>
										<Share2 className="w-6 h-6" />
									</motion.button>
								</div>
							</div>

							{/* דירוג */}
							{productStats && (
								<div className="flex items-center gap-4">
									<div className="flex items-center gap-1">
										{[...Array(5)].map((_, i) => (
											<Star
												key={i}
												className={`w-5 h-5 ${
													i < Math.floor(productStats.averageRating)
														? 'text-yellow-400 fill-current'
														: 'text-gray-300'
												}`}
											/>
										))}
									</div>
									<span className="text-gray-600">
										({productStats.totalReviews} ביקורות)
									</span>
								</div>
							)}
						</div>

						{/* מחיר */}
						<div className="bg-white p-5 rounded-2xl shadow-lg border border-gray-100">
							<div className="flex items-center gap-4 mb-4">
								<span className="text-4xl font-bold text-gray-900">
									₪{price.toFixed(2)}
								</span>
								{hasDiscount && (
									<div className="flex flex-col">
										<span className="text-xl text-gray-400 line-through">
											₪{originalPrice.toFixed(2)}
										</span>
										<span className="text-sm text-green-600 font-medium">
											חסכת ₪{(originalPrice - price).toFixed(2)}
										</span>
									</div>
								)}
							</div>
							
							{/* סטטוס מלאי */}
							<div className="flex items-center gap-2 mb-6">
								{product.inStock ? (
									<>
										<div className="w-3 h-3 bg-green-500 rounded-full"></div>
										<span className="text-green-600 font-medium">במלאי - משלוח מיידי</span>
									</>
								) : (
									<>
										<div className="w-3 h-3 bg-red-500 rounded-full"></div>
										<span className="text-red-600 font-medium">אזל מהמלאי</span>
									</>
								)}
							</div>

							{/* בורר כמות */}
							{product.inStock && (
								<div className="flex items-center gap-4 mb-6">
									<span className="text-gray-700 font-medium">כמות:</span>
									<div className="flex items-center bg-gray-100 rounded-xl">
										<button
											onClick={() => setQuantity(Math.max(1, quantity - 1))}
											className="p-2 hover:bg-gray-200 rounded-l-xl transition-colors"
										>
											<Minus className="w-4 h-4" />
										</button>
										<span className="px-4 py-2 font-medium min-w-[60px] text-center">
											{quantity}
										</span>
										<button
											onClick={() => setQuantity(quantity + 1)}
											className="p-2 hover:bg-gray-200 rounded-r-xl transition-colors"
										>
											<Plus className="w-4 h-4" />
										</button>
									</div>
								</div>
							)}

							{/* כפתור הוספה לסל */}
							<motion.button
								onClick={handleAddToCart}
								disabled={!product.inStock}
								className={`w-full py-4 px-6 rounded-xl font-bold text-lg transition-all duration-300 ${
									product.inStock
										? 'bg-gradient-to-r from-purple-600 to-purple-700 hover:from-purple-700 hover:to-purple-800 text-white shadow-lg hover:shadow-purple-500/25'
										: 'bg-gray-200 text-gray-500 cursor-not-allowed'
								}`}
								whileHover={product.inStock ? { scale: 1.02 } : {}}
								whileTap={product.inStock ? { scale: 0.98 } : {}}
							>
								<div className="flex items-center justify-center gap-3">
									<ShoppingCart className="w-6 h-6" />
									{product.inStock ? `הוסף ${quantity} לסל` : 'אזל מהמלאי'}
								</div>
							</motion.button>
						</div>

						{/* הבטחות */}
						<div className="bg-white p-5 rounded-2xl shadow-lg border border-gray-100">
							<h3 className="text-lg font-bold text-gray-900 mb-3">המוצר שלנו כולל:</h3>
							<div className="space-y-3">
								<div className="flex items-center gap-3">
									<div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
										<Truck className="w-4 h-4 text-green-600" />
									</div>
									<span className="text-gray-700"> מעל 299 ש״ח משלוח חינם בכל הארץ</span>
								</div>
								<div className="flex items-center gap-3">
									<div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
										<RotateCcw className="w-4 h-4 text-blue-600" />
									</div>
									<span className="text-gray-700">אחריות החזר כספי עד 14 ימי עבודה</span>
								</div>
								<div className="flex items-center gap-3">
									<div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center">
										<Shield className="w-4 h-4 text-purple-600" />
									</div>
									<span className="text-gray-700">אחריות שנה מלאה של יצרן</span>
								</div>
							</div>
						</div>
					</motion.div>
				</div>

				{/* טאבים עם מידע נוסף */}
				<motion.div
					className="mt-8 bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden"
					initial={{ opacity: 0, y: 30 }}
					animate={{ opacity: 1, y: 0 }}
					transition={{ duration: 0.6, delay: 0.4 }}
				>
					{/* טאבים */}
					<div className="border-b border-gray-200">
						<div className="flex">
							{[
								{ id: 'description', label: 'תיאור', icon: Info },
								{ id: 'specifications', label: 'מפרט טכני', icon: Bookmark },
								{ id: 'reviews', label: 'ביקורות', icon: MessageSquare }
							].map((tab) => (
								<button
									key={tab.id}
									onClick={() => setActiveTab(tab.id)}
									className={`flex items-center gap-2 px-6 py-4 font-medium transition-all duration-200 ${
										activeTab === tab.id
											? 'text-purple-600 border-b-2 border-purple-600 bg-purple-50'
											: 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
									}`}
								>
									<tab.icon className="w-5 h-5" />
									{tab.label}
									{tab.id === 'reviews' && productStats && (
										<span className="bg-gray-200 text-gray-700 px-2 py-1 rounded-full text-xs">
											{productStats.totalReviews}
										</span>
									)}
								</button>
							))}
						</div>
					</div>

					{/* תוכן טאבים */}
					<div className="p-6">
						<AnimatePresence mode="wait">
							{activeTab === 'description' && (
								<motion.div
									key="description"
									initial={{ opacity: 0, x: 20 }}
									animate={{ opacity: 1, x: 0 }}
									exit={{ opacity: 0, x: -20 }}
									className="prose prose-lg max-w-none"
								>
									<p className="text-gray-700 leading-relaxed">
										{product.description || 'אין תיאור זמין למוצר זה.'}
									</p>
								</motion.div>
							)}

							{activeTab === 'specifications' && (
								<motion.div
									key="specifications"
									initial={{ opacity: 0, x: 20 }}
									animate={{ opacity: 1, x: 0 }}
									exit={{ opacity: 0, x: -20 }}
									className="grid grid-cols-1 md:grid-cols-2 gap-6"
								>
									{[
										{ label: 'חומר', value: product.material },
										{ label: 'אבן', value: product.stone && product.stone !== 'none' ? product.stone : 'ללא' },
										{ label: 'משקל', value: product.weight ? `${product.weight} גרם` : null },
										{ label: 'קטגוריה', value: product.category },
										{ label: 'מין', value: product.gender === 'male' ? 'גברים' : product.gender === 'female' ? 'נשים' : 'יוניסקס' },
										{ label: 'סטטוס', value: product.productStatus }
									].filter(spec => spec.value).map((spec, index) => (
										<div key={index} className="flex justify-between items-center py-3 border-b border-gray-100 last:border-b-0">
											<span className="text-gray-600 font-medium">{spec.label}</span>
											<span className="text-gray-900">{spec.value}</span>
										</div>
									))}
								</motion.div>
							)}

							{activeTab === 'reviews' && (
								<motion.div
									key="reviews"
									initial={{ opacity: 0, x: 20 }}
									animate={{ opacity: 1, x: 0 }}
									exit={{ opacity: 0, x: -20 }}
									className="space-y-6"
								>
									{/* סטטיסטיקות ביקורות */}
									{productStats && (
										<div className="bg-gray-50 p-6 rounded-xl">
											<div className="grid grid-cols-1 md:grid-cols-3 gap-6">
												<div className="text-center">
													<div className="text-3xl font-bold text-gray-900">
														{productStats.averageRating.toFixed(1)}
													</div>
													<div className="flex justify-center gap-1 my-2">
														{[...Array(5)].map((_, i) => (
															<Star
																key={i}
																className={`w-5 h-5 ${
																	i < Math.floor(productStats.averageRating)
																		? 'text-yellow-400 fill-current'
																		: 'text-gray-300'
																}`}
															/>
														))}
													</div>
													<div className="text-gray-600">מתוך {productStats.totalReviews} ביקורות</div>
												</div>
											</div>
										</div>
									)}

									{/* כפתור כתיבת ביקורת */}
									{user && (
										<button
											onClick={() => setShowWriteReview(true)}
											className="bg-purple-600 text-white px-6 py-3 rounded-xl hover:bg-purple-700 transition-colors font-medium"
										>
											כתוב ביקורת
										</button>
									)}

									{/* רשימת ביקורות */}
									<div className="space-y-6">
										{reviews && reviews.length > 0 ? (
											reviews.map((review) => (
												<ReviewCard
													key={review._id}
													review={review}
													onVote={handleVoteOnReview}
													onDelete={handleDeleteReview}
													currentUserId={user?._id}
												/>
											))
										) : (
											<div className="text-center py-12">
												<MessageSquare className="w-16 h-16 text-gray-300 mx-auto mb-4" />
												<p className="text-gray-500">אין ביקורות עדיין למוצר זה</p>
											</div>
										)}
									</div>
								</motion.div>
							)}
						</AnimatePresence>
					</div>
				</motion.div>
			</div>

			{/* מודל תמונה */}
			<AnimatePresence>
				{showImageModal && (
					<motion.div 
						className="fixed inset-0 bg-black/90 backdrop-blur-sm flex items-center justify-center z-50 p-4"
						initial={{ opacity: 0 }}
						animate={{ opacity: 1 }}
						exit={{ opacity: 0 }}
						onClick={() => setShowImageModal(false)}
					>
						<motion.div 
							className="relative max-w-4xl max-h-[90vh] bg-white rounded-2xl overflow-hidden shadow-2xl"
							initial={{ scale: 0.8, opacity: 0 }}
							animate={{ scale: 1, opacity: 1 }}
							exit={{ scale: 0.8, opacity: 0 }}
							transition={{ type: "spring", damping: 20, stiffness: 300 }}
							onClick={(e) => e.stopPropagation()}
						>
							<button
								onClick={() => setShowImageModal(false)}
								className="absolute top-4 right-4 z-10 p-2 bg-white/90 backdrop-blur-sm rounded-full hover:bg-white transition-colors"
							>
								<svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
									<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
								</svg>
							</button>
							
							<img 
								src={images[selectedImage]} 
								alt={product.name}
								className="w-full h-full object-contain"
								style={{ maxHeight: '80vh' }}
							/>
							
							{images.length > 1 && (
								<div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex items-center gap-4 bg-white/90 backdrop-blur-sm rounded-full px-6 py-3">
									<button
										onClick={() => setSelectedImage((prev) => (prev - 1 + images.length) % images.length)}
										className="p-2 hover:bg-gray-100 rounded-full transition-colors"
									>
										<ChevronLeft className="w-5 h-5" />
									</button>
									<span className="text-sm font-medium px-4">
										{selectedImage + 1} / {images.length}
									</span>
									<button
										onClick={() => setSelectedImage((prev) => (prev + 1) % images.length)}
										className="p-2 hover:bg-gray-100 rounded-full transition-colors"
									>
										<ChevronRight className="w-5 h-5" />
									</button>
								</div>
							)}
						</motion.div>
					</motion.div>
				)}
			</AnimatePresence>

			{/* מודל כתיבת ביקורת */}
			<AnimatePresence>
				{showWriteReview && (
					<WriteReviewForm
						productId={productId}
						onClose={() => setShowWriteReview(false)}
						onSuccess={handleWriteReviewSuccess}
					/>
				)}
			</AnimatePresence>
		</div>
	);
};

export default ProductPage;